import React from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    IconButton,
    Box,
    Chip,
    Menu,
    MenuItem
} from '@mui/material';
import {
    MoreVert as MoreVertIcon,
    InsertDriveFile as FileIcon,
    Image as ImageIcon,
    VideoLibrary as VideoIcon,
    AudioFile as AudioIcon,
    PictureAsPdf as PdfIcon,
    Description as DocumentIcon,
    FolderZip as ZipIcon,
    Download as DownloadIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    DriveFileMove as MoveIcon,
    History as HistoryIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ImagePreview from './ImagePreview';

interface MediaFile {
    id: number;
    uuid: string;
    originalFilename: string;
    fileType: string;
    mimeType: string;
    size: string;
    createdAt: string;
    updatedAt: string;
    description?: string;
    categories?: Array<{ id: number; name: string; color: string }>;
}

interface FileGridProps {
    files: MediaFile[];
    onFileSelect?: (file: MediaFile) => void;
    onFileDelete?: (fileId: number) => void;
    onFileEdit?: (file: MediaFile) => void;
    onFileMove?: (file: MediaFile) => void;
    onFileVersion?: (file: MediaFile) => void;
    viewMode?: 'grid' | 'list';
}

const getFileIcon = (fileType: string, mimeType: string) => {
    switch (fileType) {
        case 'image':
            return <ImageIcon sx={{ fontSize: 60, color: '#4CAF50' }} />;
        case 'video':
            return <VideoIcon sx={{ fontSize: 60, color: '#2196F3' }} />;
        case 'audio':
            return <AudioIcon sx={{ fontSize: 60, color: '#9C27B0' }} />;
        case 'document':
            if (mimeType === 'application/pdf') {
                return <PdfIcon sx={{ fontSize: 60, color: '#F44336' }} />;
            }
            return <DocumentIcon sx={{ fontSize: 60, color: '#FF9800' }} />;
        case 'archive':
            return <ZipIcon sx={{ fontSize: 60, color: '#FFC107' }} />;
        default:
            if (mimeType === 'application/zip' || mimeType === 'application/x-zip-compressed') {
                return <ZipIcon sx={{ fontSize: 60, color: '#FFC107' }} />;
            }
            return <FileIcon sx={{ fontSize: 60, color: '#757575' }} />;
    }
};

const formatFileSize = (bytes: string): string => {
    const size = parseInt(bytes);
    if (size < 1024) return size + ' B';
    if (size < 1024 * 1024) return (size / 1024).toFixed(1) + ' KB';
    if (size < 1024 * 1024 * 1024) return (size / (1024 * 1024)).toFixed(1) + ' MB';
    return (size / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
};

const FileGridItem: React.FC<{
    file: MediaFile;
    onSelect?: () => void;
    onDelete?: () => void;
    onEdit?: () => void;
    onMove?: () => void;
    onVersion?: () => void;
}> = ({ file, onSelect, onDelete, onEdit, onMove, onVersion }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        if (onEdit) {
            onEdit();
        }
        handleMenuClose();
    };

    const handleDelete = () => {
        if (onDelete) {
            onDelete();
        }
        handleMenuClose();
    };

    const handleMove = () => {
        if (onMove) {
            onMove();
        }
        handleMenuClose();
    };

    const handleVersion = () => {
        if (onVersion) {
            onVersion();
        }
        handleMenuClose();
    };

    const handleDownload = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/media/download/${file.uuid}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = file.originalFilename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            } else {
                console.error('Download failed:', response.status);
            }
        } catch (error) {
            console.error('Error downloading file:', error);
        }
        handleMenuClose();
    };

    return (
        <>
            <Card
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    overflow: 'hidden',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 24px rgba(231, 41, 86, 0.15)',
                        borderColor: '#e72956'
                    }
                }}
                onClick={onSelect}
            >
                <Box
                    sx={{
                        height: 150,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'grey.100',
                        position: 'relative'
                    }}
                >
                    {file.fileType === 'image' ? (
                        <ImagePreview uuid={file.uuid} alt={file.originalFilename} height={150} updatedAt={file.updatedAt} />
                    ) : (
                        getFileIcon(file.fileType, file.mimeType)
                    )}

                    <IconButton
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(8px)',
                            transition: 'all 0.2s',
                            '&:hover': {
                                bgcolor: '#e72956',
                                color: 'white',
                                transform: 'scale(1.1)'
                            }
                        }}
                        size="small"
                        onClick={handleMenuOpen}
                    >
                        <MoreVertIcon />
                    </IconButton>
                </Box>

                <CardContent sx={{ flexGrow: 1 }}>
                    <Typography
                        variant="subtitle2"
                        noWrap
                        title={file.originalFilename}
                        sx={{ fontWeight: 600, mb: 1 }}
                    >
                        {file.originalFilename}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Chip
                            label={file.fileType}
                            size="small"
                            sx={{
                                fontSize: '0.7rem',
                                height: 20,
                                bgcolor: '#e72956',
                                color: 'white',
                                fontWeight: 600
                            }}
                        />
                        <Chip
                            label={formatFileSize(file.size)}
                            size="small"
                            variant="outlined"
                            sx={{
                                fontSize: '0.7rem',
                                height: 20,
                                borderColor: '#1f3240',
                                color: '#1f3240'
                            }}
                        />
                    </Box>

                    {file.description && (
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{
                                mt: 1,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                            }}
                        >
                            {file.description}
                        </Typography>
                    )}

                    {file.categories && file.categories.length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {file.categories.slice(0, 2).map((cat) => (
                                <Chip
                                    key={cat.id}
                                    label={cat.name}
                                    size="small"
                                    sx={{
                                        bgcolor: cat.color,
                                        color: 'white',
                                        fontSize: '0.7rem',
                                        height: 20
                                    }}
                                />
                            ))}
                            {file.categories.length > 2 && (
                                <Chip
                                    label={`+${file.categories.length - 2}`}
                                    size="small"
                                    sx={{
                                        fontSize: '0.7rem',
                                        height: 20,
                                        bgcolor: '#f5f5f5',
                                        color: '#1f3240'
                                    }}
                                />
                            )}
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleEdit}>
                    <EditIcon fontSize="small" sx={{ mr: 1 }} />
                    Bearbeiten
                </MenuItem>
                <MenuItem onClick={handleVersion}>
                    <HistoryIcon fontSize="small" sx={{ mr: 1 }} />
                    Versionsverlauf
                </MenuItem>
                <MenuItem onClick={handleMove}>
                    <MoveIcon fontSize="small" sx={{ mr: 1 }} />
                    Verschieben
                </MenuItem>
                <MenuItem onClick={handleDownload}>
                    <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
                    Herunterladen
                </MenuItem>
                <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                    Löschen
                </MenuItem>
            </Menu>
        </>
    );
};

const FileGrid: React.FC<FileGridProps> = ({ files, onFileSelect, onFileDelete, onFileEdit, onFileMove, onFileVersion, viewMode = 'grid' }) => {
    if (files.length === 0) {
        return (
            <Box
                sx={{
                    height: 400,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary'
                }}
            >
                <Box sx={{ textAlign: 'center' }}>
                    <FileIcon sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
                    <Typography variant="h6">Keine Dateien vorhanden</Typography>
                    <Typography variant="body2">Laden Sie Dateien hoch, um sie hier zu sehen</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Grid container spacing={2}>
            {files.map((file) => (
                <Grid
                    key={file.id}
                    size={viewMode === 'grid' ? { xs: 12, sm: 6, md: 4, lg: 3 } : { xs: 12 }}
                >
                    <FileGridItem
                        file={file}
                        onSelect={() => onFileSelect && onFileSelect(file)}
                        onDelete={() => onFileDelete && onFileDelete(file.id)}
                        onEdit={() => onFileEdit && onFileEdit(file)}
                        onMove={() => onFileMove && onFileMove(file)}
                        onVersion={() => onFileVersion && onFileVersion(file)}
                    />
                </Grid>
            ))}
        </Grid>
    );
};

export default FileGrid;
